<!-- SubscriptionForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Store
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useUserStore } from "@/stores/useUserStore";

// Componentes modais
import AddSubscriptionForm from "./AddSubscriptionForm.vue";
import EditSubscriptionForm from "./EditSubscriptionForm.vue";
import DeleteSubscriptionForm from "./DeleteSubscriptionForm.vue";
import BatchDeleteSubscriptionForm from "./BatchDeleteSubscriptionForm.vue";
import SubscriptionCardModal from "./SubscriptionCardModal.vue";

// Busca
import SearchSubscriptionInput from "./SearchSubscriptionInput.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

import ExpireOverdueConfirmModal from "./ExpireOverdueConfirmModal.vue";
import ExpireOverdueResultModal from "./ExpireOverdueResultModal.vue";

const subscriptionStore = useSubscriptionStore();
const userStore = useUserStore();

const isSys = computed(() => userStore.currentUser?.userName === "Sys");

const callerId = computed(() =>
  userStore.currentUser?.userId != null
    ? String(userStore.currentUser.userId)
    : null
);

// Estados dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const selectedSubscription = ref(null);

const showExpireOverdueConfirmModal = ref(false);
const showExpireOverdueResultModal = ref(false);
const expireOverdueResult = ref(null);

// modal de card
const showCardModal = ref(false);
const selectedCardSubscriptionId = ref(null);

function handleOpenCard(sub) {
  selectedCardSubscriptionId.value = sub.subscriptionId;
  showCardModal.value = true;
}

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// loading visual da tabela
const isLoadingTable = ref(false);

// Dropdowns
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// TOAST
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// NORMALIZAÇÃO DE STATUS
function normalizeSubscriptionStatus(status) {
  if (status === undefined || status === null) return "pendingpayment";
  const s = String(status).trim().toLowerCase().replace(/[_\s]/g, "");
  const map = {
    active: "active",
    cancelled: "cancelled",
    expired: "expired",
    upgraded: "upgraded",
    pendingpayment: "pendingpayment",
    0: "active",
    1: "cancelled",
    2: "expired",
    3: "upgraded",
    4: "pendingpayment",
  };
  return map[s] ?? "pendingpayment";
}

function getSubscriptionStatusLabel(status) {
  const s = normalizeSubscriptionStatus(status);
  const labels = {
    active: "Ativa",
    cancelled: "Cancelada",
    expired: "Expirada",
    upgraded: "Atualizada",
    pendingpayment: "Aguardando",
  };
  return labels[s] ?? "Aguardando";
}

function getSubscriptionStatusClass(status) {
  const s = normalizeSubscriptionStatus(status);
  const classes = {
    active: "status-tag status-green",
    cancelled: "status-tag status-red",
    expired: "status-tag status-orange",
    upgraded: "status-tag status-blue",
    pendingpayment: "status-tag status-yellow",
  };
  return classes[s] ?? "status-tag status-yellow";
}

function normalizePaymentStatus(status) {
  if (status === undefined || status === null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    approved: "approved",
    rejected: "rejected",
    cancelled: "cancelled",
    refunded: "refunded",
    refundfailed: "refundfailed",
    0: "none",
    1: "pending",
    2: "approved",
    3: "rejected",
    4: "cancelled",
    5: "refunded",
    6: "refundfailed",
  };
  return map[s] ?? "none";
}

function getPaymentStatusLabel(status, paymentMethod) {
  if (
    normalizePaymentStatus(status) === "approved" &&
    paymentMethod?.toLowerCase() === "courtesy"
  ) {
    return "—";
  }

  const s = normalizePaymentStatus(status);
  const labels = {
    none: "—",
    pending: "Pendente",
    approved: "Aprovado",
    rejected: "Rejeitado",
    cancelled: "Cancelado",
    refunded: "Estornado",
    refundfailed: "Revisar estorno",
  };
  return labels[s] ?? "—";
}

function getPaymentStatusClass(status, paymentMethod) {
  if (
    normalizePaymentStatus(status) === "approved" &&
    paymentMethod?.toLowerCase() === "courtesy"
  ) {
    return "status-tag status-gray";
  }

  const s = normalizePaymentStatus(status);
  const classes = {
    none: "status-tag status-gray",
    pending: "status-tag status-yellow",
    approved: "status-tag status-green",
    rejected: "status-tag status-red",
    cancelled: "status-tag status-red",
    refunded: "status-tag status-purple",
    refundfailed: "status-tag status-orange",
  };
  return classes[s] ?? "status-tag status-gray";
}

function normalizePreapprovalStatus(status) {
  if (status === undefined || status === null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    authorized: "authorized",
    cancelled: "cancelled",
    paused: "paused", // ✅ NOVO
    0: "none",
    1: "pending",
    2: "authorized",
    3: "cancelled",
    4: "paused", // ✅ NOVO — corresponde a PreapprovalStatus.Paused = 4 no backend
  };
  return map[s] ?? "none";
}

function getPreapprovalStatusLabel(status) {
  const s = normalizePreapprovalStatus(status);
  const labels = {
    none: "—",
    pending: "Pendente",
    authorized: "Autorizado",
    cancelled: "Cancelado",
    paused: "Pausado", // ✅ NOVO
  };
  return labels[s] ?? "—";
}

function getPreapprovalStatusClass(status) {
  const s = normalizePreapprovalStatus(status);
  const classes = {
    none: "status-tag status-gray",
    pending: "status-tag status-yellow",
    authorized: "status-tag status-green",
    cancelled: "status-tag status-red",
    paused: "status-tag status-orange", // ✅ NOVO — laranja diferencia bem de cancelled (vermelho)
  };
  return classes[s] ?? "status-tag status-gray";
}

// PERMISSÕES
function canEdit() {
  return true;
}
function canDelete() {
  return true;
}

const anyEditableOnPage = computed(() =>
  subscriptionStore.filteredSubscriptions.some(() => canEdit())
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIds = subscriptionStore.filteredSubscriptions
      .filter(() => canEdit())
      .map((s) => s.subscriptionId);
    if (editableIds.length === 0) return false;
    return editableIds.every((id) =>
      subscriptionStore.selectedSubscriptionIds.includes(id)
    );
  },
  set: (value) => {
    const editableIds = subscriptionStore.filteredSubscriptions
      .filter(() => canEdit())
      .map((s) => s.subscriptionId);

    const allIds = subscriptionStore.filteredSubscriptions.map(
      (s) => s.subscriptionId
    );

    if (value) {
      editableIds.forEach((id) => {
        if (!subscriptionStore.selectedSubscriptionIds.includes(id))
          subscriptionStore.selectedSubscriptionIds.push(id);
      });
    } else {
      subscriptionStore.selectedSubscriptionIds =
        subscriptionStore.selectedSubscriptionIds.filter(
          (id) => !allIds.includes(id)
        );
    }
  },
});

const canBatchActions = computed(
  () =>
    Array.isArray(subscriptionStore.selectedSubscriptionIds) &&
    subscriptionStore.selectedSubscriptionIds.length > 0
);

// AÇÕES DE LINHA
function toggleRowDropdown(id) {
  openDropdownId.value = openDropdownId.value === id ? null : id;
}

function handleEdit(sub) {
  selectedSubscription.value = sub;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(sub) {
  handleOpenCard(sub);
  openDropdownId.value = null;
}

function handleDelete(sub) {
  selectedSubscription.value = sub;
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  subscriptionStore.toggleSelection(id);
}

// FETCH
async function fetchSubscriptions() {
  try {
    isLoadingTable.value = true;

    if (!isSys.value && callerId.value) {
      subscriptionStore.filters.userId = callerId.value;
    }

    await subscriptionStore.fetchSubscriptionsPaged(
      currentPage.value,
      pageSize.value,
      subscriptionStore.userIdFilter
    );
  } catch (error) {
    console.error("❌ Erro ao buscar assinaturas:", error?.message);
    showErrorToast("Erro ao carregar assinaturas.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchSubscriptions);

// BATCH DELETE
function tryOpenBatchDeleteModal() {
  if (
    !Array.isArray(subscriptionStore.selectedSubscriptionIds) ||
    subscriptionStore.selectedSubscriptionIds.length === 0
  ) {
    showErrorToast("Nenhuma assinatura selecionada.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

async function handleExpireOverdueConfirm() {
  const result = await subscriptionStore.expireOverdueAsync();
  return result;
}

function handleExpireOverdueSuccess(result) {
  expireOverdueResult.value = result;
  showExpireOverdueResultModal.value = true;
}

// HANDLER PÓS-MUDANÇA
async function handleSubscriptionsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) subscriptionStore.clearSelection();

  if (successMessage) showSuccessToast(successMessage);

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchSubscriptions();
    return;
  }

  await fetchSubscriptions();
}

// CLICK FORA
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  if (!isSys.value && callerId.value) {
    subscriptionStore.filters.userId = callerId.value;
    subscriptionStore.userIdFilter = Number(callerId.value);
  }

  fetchSubscriptions();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  isLoadingTable.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Assinaturas</div>
          <div class="text-sm mt-1 text-gray-700 ">
            <template v-if="isSys">
              Gerencie as assinaturas do sistema, acompanhe status de pagamento,
              métodos de pagamento e datas de vigência de cada assinatura.
            </template>
            <template v-else>
              Acompanhe suas assinaturas, status de pagamento, métodos de
              pagamento e datas de vigência.
            </template>
          </div>
        </div>
      </div>

      <div class="action-bar action-bar-stacked">
        <div class="w-full">
          <SearchSubscriptionInput :is-sys="isSys" />
        </div>

        <div
          class="w-full flex flex-row items-center justify-end space-x-3 flex-shrink-0"
        >
          <template v-if="isSys">
            <button
              @click="showExpireOverdueConfirmModal = true"
              class="btn btn-expire"
            >
              <svg
                class="h-4 w-4 mr-1.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clip-rule="evenodd"
                />
              </svg>
              Expirar Vencidas
            </button>

            <button @click="showAddModal = true" class="btn btn-primary">
              <svg
                class="h-4 w-4 mr-1.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                />
              </svg>
              Adicionar Assinatura
            </button>

            <div ref="batchDropdownRef" class="relative">
              <button
                @click="isBatchDropdownOpen = !isBatchDropdownOpen"
                class="btn btn-batch w-full"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <a
                    href="#"
                    role="button"
                    @click.prevent="tryOpenBatchDeleteModal"
                    class="dropdown-item danger-item"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionadas</span>
                  </a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 60px">Ações</th>
              <th style="width: 5%">ID</th>
              <th style="width: 6%">Usuário</th>
              <th style="width: 6%">Plano</th>
              <th style="width: 9%">Método</th>
              <th style="width: 9%">MP Pay ID</th>
              <th style="width: 9%">Status</th>
              <th style="width: 9%">Pagamento</th>
              <th style="width: 9%">Preapproval</th>
              <th style="width: 11%">Início</th>
              <th style="width: 11%">Fim</th>
              <th style="width: 12%">Detalhe</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="sub in !isLoadingTable ? subscriptionStore.filteredSubscriptions : []"
              :key="sub.subscriptionId"
              class="clickable-row"
              @click="handleOpenCard(sub)"
            >
              <td @click.stop>
                <input
                  type="checkbox"
                  :value="sub.subscriptionId"
                  :checked="
                    subscriptionStore.selectedSubscriptionIds?.includes(
                      sub.subscriptionId
                    )
                  "
                  @change="toggleSelection(sub.subscriptionId)"
                  class="checkbox-style"
                  :disabled="!canEdit()"
                  :class="{ 'disabled-item': !canEdit() }"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(sub.subscriptionId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === sub.subscriptionId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      v-if="isSys"
                      :class="{ 'disabled-item': !canEdit() }"
                      @click="canEdit() && handleEdit(sub)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(sub)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      v-if="isSys"
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete() }"
                      @click="canDelete() && handleDelete(sub)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="sub.subscriptionId">{{ sub.subscriptionId }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="sub.userId">{{ sub.userId }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="sub.planName ?? sub.planId">
                  {{ sub.planName ?? sub.planId }}
                </span>
              </td>

              <td class="cell-text-center">
                <span
                  v-if="sub.paymentMethod?.toLowerCase() === 'courtesy'"
                  class="courtesy-method-tag"
                  title="Cortesia"
                >
                  <i class="fas fa-gift"></i><span>Courtesy</span>
                </span>
                <span v-else :title="sub.paymentMethod ?? '—'">
                  {{ sub.paymentMethod ?? "—" }}
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="sub.mpPaymentId ?? '—'">
                  {{ sub.mpPaymentId ?? "—" }}
                </span>
              </td>

              <td>
                <span :class="getSubscriptionStatusClass(sub.status)">
                  {{ getSubscriptionStatusLabel(sub.status) }}
                </span>
              </td>

              <td>
                <span
                  :class="
 getPaymentStatusClass(sub.paymentStatus, sub.paymentMethod)
                  "
                >
                  {{
                    getPaymentStatusLabel(sub.paymentStatus, sub.paymentMethod)
                  }}
                </span>
              </td>

              <td>
                <span :class="getPreapprovalStatusClass(sub.preapprovalStatus)">
                  {{ getPreapprovalStatusLabel(sub.preapprovalStatus) }}
                </span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(sub.startDate)">
                  {{ formatDateTimeForDisplay(sub.startDate) }}
                </span>
              </td>

              <td>
                <span
                  :title="
                    sub.endDate ? formatDateTimeForDisplay(sub.endDate) : '—'
                  "
                >
                  {{
                    sub.endDate ? formatDateTimeForDisplay(sub.endDate) : "—"
                  }}
                </span>
              </td>

              <td>
                <span :title="sub.statusDetail ?? '—'">
                  {{ sub.statusDetail ?? "—" }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="13" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando assinaturas...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(subscriptionStore.totalCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="13" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhuma assinatura encontrada.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (subscriptionStore.totalCount ?? 0) > 0"
          class="sub-pagination-row"
        >
          <div class="sub-pagination-left">
            <span class="sub-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="subscriptionStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="subscriptionStore.totalCount ?? 0"
            :total-pages="subscriptionStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <SubscriptionCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :subscription-id="selectedCardSubscriptionId"
      @close="showCardModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <AddSubscriptionForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="
        handleSubscriptionsChanged({ goToFirstPage: true, closeAdd: true })
      "
    />

    <EditSubscriptionForm
      v-if="showEditModal"
      :subscription="selectedSubscription"
      @close="showEditModal = false"
      @refresh="
        handleSubscriptionsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <DeleteSubscriptionForm
      v-if="showDeleteModal"
      :subscription="selectedSubscription"
      @close="showDeleteModal = false"
      @refresh="
        handleSubscriptionsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeleteSubscriptionForm
      v-if="showBatchDeleteModal"
      :subscriptionIds="subscriptionStore.selectedSubscriptionIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleSubscriptionsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <ExpireOverdueConfirmModal
      v-if="showExpireOverdueConfirmModal"
      :show="showExpireOverdueConfirmModal"
      :on-confirm="handleExpireOverdueConfirm"
      @close="showExpireOverdueConfirmModal = false"
      @success="handleExpireOverdueSuccess"
    />

    <ExpireOverdueResultModal
      v-if="showExpireOverdueResultModal && expireOverdueResult"
      :show="showExpireOverdueResultModal"
      :result="expireOverdueResult"
      @close="showExpireOverdueResultModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.action-bar-stacked {
  flex-direction: column !important;
  align-items: stretch !important;
}

.btn-expire {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 0.9rem;
  font-size: 0.85rem;
  border-radius: 10px;
  border: none;
  background-color: var(--warning-bg);
  color: var(--warning-text);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  gap: 0.35rem;
  border: 1px solid var(--warning-bg);
}

.btn-expire:hover {
  background-color: var(--warning-bg);
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
}

.cell-text-center {
  text-align: center;
}

.status-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}

.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}

.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}

.status-yellow {
  background-color: var(--warning-bg);
  color: #854d0e;
}

.status-orange {
  background-color: #ffe8cc;
  color: #9c4a00;
}

.status-blue {
  background-color: #d0ebff;
  color: #1864ab;
}

.status-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}

.status-gray {
  background-color: #f1f3f5;
  color: #495057;
}

.sub-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}

.sub-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.sub-pagination-label {
  padding-top: 2px;
}

.courtesy-method-tag {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 9999px;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: 1px solid #c4b5fd;
  white-space: nowrap;
}

.courtesy-method-tag i {
  margin-right: 0.4rem;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>